import List from '@components/v2/ui/List/List';
import ListItemButton from '@components/v2/ui/List/ListItemButton';
import Popover from '@components/v2/ui/Popover/Popover';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import { ComponentType } from 'react';
import { CatalogTestIds } from '../../../../utils/testIds/catalog.testIds';
import { useTranslation } from '../../../translation/TranslationContext';
import {
  CATALOG_CREATION_CRITERIA_CONFIGS,
  CATALOG_CREATION_CRITERIA_POPOVER_MAX_HEIGHT,
  CATALOG_CREATION_CRITERIA_POPOVER_WIDTH,
} from '../catalogCreationCriteria.consts';
import {
  CatalogCreationCriteriaSourcePickerProps,
  CatalogCreationCriterionEnum,
} from '../catalogCreationCriteria.types';
import CatalogCreationAvailabilityCriteriaPicker from './CatalogCreationAvailabilityCriteriaPicker';
import CatalogCreationClassificationCriteriaPicker from './CatalogCreationClassificationCriteriaPicker';
import CatalogCreationCollectionCriteriaPicker from './CatalogCreationCollectionCriteriaPicker';

const PICKER_BY_CRITERION: Record<
  CatalogCreationCriterionEnum,
  ComponentType<CatalogCreationCriteriaSourcePickerProps>
> = {
  [CatalogCreationCriterionEnum.AVAILABILITY]: CatalogCreationAvailabilityCriteriaPicker,
  [CatalogCreationCriterionEnum.COLLECTION]: CatalogCreationCollectionCriteriaPicker,
  [CatalogCreationCriterionEnum.CLASSIFICATION]: CatalogCreationClassificationCriteriaPicker,
};

interface CatalogCreationCriteriaPopoverProps extends CatalogCreationCriteriaSourcePickerProps {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  activeCriterion: CatalogCreationCriterionEnum | null;
  onClose: () => void;
  onSelectCriterion: (criterion: CatalogCreationCriterionEnum) => void;
}

const CatalogCreationCriteriaPopover = ({
  isOpen,
  anchorEl,
  activeCriterion,
  onClose,
  onSelectCriterion,
  ...pickerProps
}: CatalogCreationCriteriaPopoverProps) => {
  const { translate } = useTranslation();

  const ActivePicker = activeCriterion ? PICKER_BY_CRITERION[activeCriterion] : null;

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      testId={CatalogTestIds.CATALOG_CREATION_MODAL_CRITERIA_POPOVER}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: (theme) => ({
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            overscrollBehavior: 'none',
            width: CATALOG_CREATION_CRITERIA_POPOVER_WIDTH,
            maxHeight: CATALOG_CREATION_CRITERIA_POPOVER_MAX_HEIGHT,
            marginTop: theme.spacing(1),
            borderRadius: `${theme.shape.borderRadius2xl}px`,
            border: `1px solid ${theme.palette.custom.border.primary}`,
          }),
        },
      }}
    >
      {ActivePicker ? (
        <ActivePicker {...pickerProps} />
      ) : (
        <Box sx={{ p: 0.75 }}>
          <List>
            {CATALOG_CREATION_CRITERIA_CONFIGS.map(({ criterion, typeLabel }) => (
              <ListItemButton
                key={criterion}
                testId={`${CatalogTestIds.CATALOG_CREATION_MODAL_CRITERIA_TYPE_PREFIX}${criterion}`}
                onClick={() => onSelectCriterion(criterion)}
              >
                <Typography component="span" variant="body2">
                  {translate(typeLabel)}
                </Typography>
              </ListItemButton>
            ))}
          </List>
        </Box>
      )}
    </Popover>
  );
};

export default CatalogCreationCriteriaPopover;
